import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LeadSourcesService } from "../../../services/user/leadsources.services"
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";

const AddLeadSourceComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const leadSourcesId = location.state?.leadSourcesId || 0;
    const isEditMode = !!leadSourcesId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            leadSourcesId: 0,
            leadSourceName: "",
            isActive: true,
            storeId: selectedStore,
        },
        validationSchema: Yup.object({
            leadSourceName: Yup.string().required("Please enter a valid name"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const data = { ...values, isActive: values.isActive === true ? true : false }
                if (isEditMode) {
                    await LeadSourcesService.updateLeadSourceById(leadSourcesId, data)
                        .then((response) => {
                            if (response) {
                                navigate("/leadsources");
                            }
                        });
                } else {
                    await LeadSourcesService.addNewLeadSource(data)
                        .then((response) => {
                            if (response) {
                                navigate("/leadsources");
                            }
                        });
                }
            } catch (error) {
                console.error("Error while submitting form:", error);
                handleNotify(
                    "An error occurred while saving the lead source.",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchLeadSourcesDetail = async () => {
                try {
                    const data = await LeadSourcesService.getLeadSourceById(leadSourcesId)
                    formik.setValues({
                        leadSourceName: data?.leadSourceName,
                        isActive: data?.isActive,
                    })
                }
                catch (error) {
                    console.log('Error fetching country details', error)
                }
            }
            fetchLeadSourcesDetail()
        }
    }, [])

    const handleBackClick = () => {
        navigate("/leadsources");
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Lead Sources</div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Lead Source" : "New Lead Source"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            {/* Lead Source Name Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="leadSourceName" className="form-label d-flex">
                                        Lead Source Name <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="leadSourceName"
                                        name="leadSourceName"
                                        className={`form-control ${formik.touched.leadSourceName && formik.errors.leadSourceName ? "is-invalid" : ""}`}
                                        placeholder="Enter Lead Source Name"
                                        value={formik.values.leadSourceName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.leadSourceName && formik.errors.leadSourceName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.leadSourceName}</div>
                                    )}
                                </div>
                            </div>

                            {/* Status Radio Buttons */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="form-check form-check-inline me-3">
                                    <input
                                        type="radio"
                                        id="active"
                                        name="isActive"
                                        className="form-check-input"
                                        value={true}
                                        onChange={() => formik.setFieldValue("isActive", true)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === true}
                                    />
                                    <label className="form-check-label" htmlFor="active">
                                        Active
                                    </label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        type="radio"
                                        id="inactive"
                                        name="isActive"
                                        className="form-check-input"
                                        value={false}
                                        onChange={() => formik.setFieldValue("isActive", false)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === false}
                                    />
                                    <label className="form-check-label" htmlFor="inactive">
                                        Inactive
                                    </label>
                                </div>
                            </div>
                            {/* Submit */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddLeadSourceComponent;
